import type { IAction, IActionDefinitionDto, IDisplayItem, IEvent, IScreenDesign } from "@kuika/kuika-cl-model";
import { KMainFunctions } from "./main-functions";

/**
 * Every action INSTANCE defined anywhere on ONE screen, labelled from the action definitions.
 *
 * WHY THIS IS A FUNCTION OF A SCREEN. This walk used to live only inside the screen provider,
 * reading `this.getScreenDesign()` — that is, always the screen the designer has OPEN. So
 * everything asking "which actions exist here?" was necessarily looking at the open canvas. A
 * detached screen session (the workflow screen builder edits a task screen WITHOUT opening it)
 * needs the same answer about a DIFFERENT screen, and answering from the open one would bind a
 * property to an action id that does not exist on the screen being written — the wrong-canvas
 * failure this platform has already paid for more than once.
 *
 * The screen is therefore a parameter and the provider delegates here. Semantics are unchanged,
 * INCLUDING the two deliberate ones:
 *
 *   - Actions are MUTATED to carry `label`/`description` from their definition. Callers match
 *     actions by label (the property data editor resolves an options action that way), so the
 *     labelling is part of the contract, not a display nicety.
 *   - Dedup is by definition id + tag, EXCEPT `IfThenElse` containers, which dedup by INSTANCE
 *     id: every container shares one definition, so a definition-based dedup dropped all but the
 *     first and hid the actions nested inside it (KOA-17919).
 */
export function collectDefinedActions(
  screen: IScreenDesign | undefined,
  actionDefinitions: readonly IActionDefinitionDto[] | undefined
): IAction[] {
  if (!screen || KMainFunctions.isObjectEmpty(screen)) return [];
  const definitions: readonly IActionDefinitionDto[] = actionDefinitions ?? [];
  let actions: IAction[] = [];
  (screen.displayItems ?? []).forEach((di: IDisplayItem): void => {
    actions = collectDefinedActionsByDi(actions, di, definitions);
  });
  return actions;
}

/** The per-display-item half of {@link collectDefinedActions} — recurses into children. */
export function collectDefinedActionsByDi(
  refActions: IAction[],
  di: IDisplayItem | undefined,
  actionDefinitions: readonly IActionDefinitionDto[]
): IAction[] {
  let collected: IAction[] = refActions;
  const events: IEvent[] | undefined = di?.item?.events;
  if (events) {
    events.forEach((event: IEvent): void => {
      if (!event.actions) return;
      event.actions.forEach((action: IAction): void => {
        const actionDef: IActionDefinitionDto | undefined = actionDefinitions.find(
          (candidate: IActionDefinitionDto): boolean => candidate.id === action.actionDefinitionId
        );
        if (actionDef) {
          action.label = actionDef.name;
          action.description = actionDef.name;
        }
        const isAlreadyCollected: boolean = action.ifThenElseData
          ? collected.some((refAction: IAction): boolean => refAction.id === action.id)
          : collected.some(
              (refAction: IAction): boolean =>
                refAction.actionDefinitionId === action.actionDefinitionId &&
                (action.tag || "") === (refAction.tag || "")
            );
        if (!isAlreadyCollected) collected.push(action);

        const recurring: IAction[] | undefined = action.recurringActionsData?.recurringActions;
        if (recurring) {
          recurring.forEach((recurringAction: IAction): void => {
            const recurringActionDef: IActionDefinitionDto | undefined = actionDefinitions.find(
              (candidate: IActionDefinitionDto): boolean => candidate.id === recurringAction.actionDefinitionId
            );
            if (recurringActionDef) {
              recurringAction.label = recurringActionDef.name;
              recurringAction.description = recurringActionDef.name;
            }
            const isRecurringCollected: boolean = collected.some(
              (refAction: IAction): boolean =>
                refAction.actionDefinitionId === recurringAction.actionDefinitionId &&
                (recurringAction.tag || "") === (refAction.tag || "")
            );
            if (!isRecurringCollected) collected.push(recurringAction);
          });
        }
      });
    });
  }
  const children: IDisplayItem[] | undefined = di?.children;
  if (children && children.length > 0) {
    children.forEach((childDi: IDisplayItem): void => {
      collected = collectDefinedActionsByDi(collected, childDi, actionDefinitions);
    });
  }
  return collected;
}
